import apisauce from 'apisauce'
import merge from 'lodash/merge'
import {ONE_SECOND_MS} from "helpers/dates"
import {toArray} from 'helpers/string'
import generateUUID from '../helpers/uuid'
import {getFailureCode, getFailureMessage} from 'helpers/api'
import {INPUT_LEVELS} from "helpers/pricingCalculator"

export const addProperty = (prop, value = true) => {
  return {
    [prop]: value,
  }
}

export const addProperties = (props, sep = ',') => {
  if (typeof props === 'string') {
    props = toArray(props, sep)
  }
  return merge(props.map(p => addProperty(p)))
}

export const serviceCall = (func, params = {}, fields, uuid = generateUUID()) => {
  fields = fields || addProperty('default')
  const ret = {}
  ret[func] = {
    params,
    fields,
    uuid,
  }
  return ret
}

// Requests a single object given its ID from the given API
// There will be a single service call response in response.data.data
// And a single object or NULL in response.data.data.result
export const getOne = function* (api, id, fields, moreParams, uuid) {
  // response should have a single service call response in data.data
  const response = yield api.get({id, ...moreParams}, fields, null, uuid)
  if (response.ok && response.data) {
    let result = response.data.data[0].result
    if (result !== null && result.constructor === Array && result.length > 0) {
      result = result[0]
    } else {
      result = null
    }
    response.data.data[0].result = result
  }
  return response
}

// Requests a single object given the params from the given API
// There will be a single service call response in response.data.data
// And a single object or NULL in response.data.data.result
export const getOneWithParams = function* (api, params, fields, uuid) {
  // response should have a single service call response in data.data
  const response = yield api.get(params, fields, uuid)
  if (response.ok && response.data) {
    let result = response.data.data[0].result
    if (result !== null && result.constructor === Array && result.length > 0) {
      result = result[0]
    } else {
      result = null
    }
    response.data.data[0].result = result
  }
  return response
}

const mapSingleResponse = (call) => {
  call.then(ret => {
    if (ret.ok && ret.data) {
      let result = ret.data.data[0].result
      if (result !== null && result.constructor === Array && result.length > 0) {
        result = result[0]
      } else {
        result = null
      }
      ret.data.data[0].result = result
    }
  })
  return call
}

export const createAPI = (url) => apisauce.create({
  baseURL: url,
  // default headers for all requests
  headers: {
    'Cache-Control': 'no-cache',
    'X-Go-Client': 'Browser',
  },
  timeout: 30 * ONE_SECOND_MS,
})

export const createPublicAPI = (url, token) => apisauce.create({
  baseURL: url,
  headers: {
    'Cache-Control': 'no-cache',
    'Authorization': `Bearer ${token}`,
    'X-Go-Client': 'Browser',
  },
  timeout: 30 * ONE_SECOND_MS,
})

export const multipartConfig = {
  headers: {
    'Cache-Control': 'no-cache',
    'X-Go-Client': 'Browser',
    'Content-Type': 'multipart/form-data',
  },
  timeout: 30 * ONE_SECOND_MS,
}

export const emailMultipartConfig = {
  ...multipartConfig,
  timeout: 300 * ONE_SECOND_MS,
}

const create = (baseURL = '/service') => {
  const api = createAPI(baseURL)

  // Maps a single service call request and response
  const callOne = (serviceCall, config = {}) => {
    let response = call([serviceCall], config)
    response.then(function (result) {
      if (result.data && result.data.length > 0) {
        response = {...result.data, data: result.data[0]}
      } else {
        response = {...result.data, data: null}
      }

    })
    return response
  }

  const call = (serviceCalls, config = {}) => {
    let response = api.post('api/call?' + Object.keys(serviceCalls[0])[0], serviceCalls, config)
    // re-map .data.data to .data for simplicity
    response.then(function (result) {
      if (result & result.data && result.data.data) {
        response = {...result.data, data: result.data.data}
      }
    })
    return response
  }

  // Generic handler for API responses
  // response: the response from the API
  // onOk: (key, data): called for each successful service request with the data as responded by the
  //                    server for this service call
  // onFail: (key, message, code): called for each failed service request with a message/error and
  //                               code if provided by the server, or called once with key=NULL if a
  //                               general error occurred
  function* handleResponse(response, onOk, onFail) {

    if (!response.ok) {
      const message = getFailureMessage(response)
      const code = getFailureCode(response)
      return yield onFail(null, message, code)
    }

    if (response.data == null)
      return yield onFail(null, 'No response from server')

    // array of service call responses or single service call response
    const data = response.data.data

    if (data != null && data.constructor === Array) {
      if (data.length === 0)
        return yield onFail(null, 'Invalid response from server')

      const call = data[0]
      if (call.success) {
        return yield onOk(0, call)
      } else {
        return yield onFail(0, call.message, call.errorCode)
      }
    }

    if (data != null && typeof (data) === 'object') {
      if (data.success) {
        return yield onOk(0, data)
      } else {
        return yield onFail(0, data.message, data.errorCode)
      }
    }

    return yield onFail(null, 'Invalid response from server')
  }

  // A version of handleResponse using direct callbacks rather than yields
  const handleResponseNonYield = (response, onOk, onFail) => {
    if (!response.ok) {
      let message = response.problem
      let code = response.status

      // Check for a returned message anyway
      const data = response.data // array of service call responses or single service call response
      if (data && data.message) message = data.message
      if (data && data.errorCode) code = data.errorCode

      onFail(null, message, code)
    } else if (response.data == null) {
      onFail(null, 'No response from server')
    } else {
      const data = response.data.data // array of service call responses or single service call response
      if (data != null && data.constructor === Array) {
        for (let index = 0; index < data.length; index++) {
          const call = data[index]
          if (call.success) {
            onOk(index, call)
          } else {
            onFail(index, call.message, call.errorCode)
          }
        }
      } else if (data != null && typeof (data) === 'object') {
        if (data.success) {
          onOk(0, data)
        } else {
          onFail(0, data.message, data.errorCode)
        }
      } else {
        onFail(null, 'Invalid response from server')
      }
    }
  }

  // A version of handleResponse which returns an array of structures for each response, in the format:
  // {
  //  success: true if successful, false if not
  //  data: the data in the response (if successful)
  //  failMessage: the message if the call failed
  //  failCode: the code if the call failed
  // }
  const getResponse = (response) => {
    const ret = []
    handleResponseNonYield(
      response,
      (index, data) => {
        ret[index] = {
          success: true,
          data,
        }
      },
      (index, message, code) => {
        ret[index] = {
          success: false,
          failMessage: message,
          failCode: code,
        }
      }
    )
    return ret
  }

  const getSingleResponse = (response) => {
    const ret = getResponse(response)
    if (ret && ret.length > 0) return ret[0]
    return {success: false}
  }

  const login = (username, password, deviceFingerprint, token) =>
    api.post('auth/login', {
      username,
      password,
      deviceFingerprint,
      token,
      force: true,
      deviceName: 'Browser',
    })

  const authenticateTfaWithTotp = (code, deviceFingerprint, trustDevice) => {
    return api.post(
      "auth/login-totp",
      {
        deviceFingerprint,
        code,
        trustDevice,
      }
    )
  }

  const refreshAuth = (username, password) => api.post('auth/refresh', {username, password})

  const refreshAuthTotp = (token, totpCode) => api.post('auth/refresh-totp', {token, totpCode})

  const userLoad = () => api.post('auth/user-load', {})

  const sessionLogin = () => api.get('auth/login-session', {})

  const sessionCheck = () => api.get('auth/session-check', {})

  const signUp_202011 = (username, password, company, fullname, country, countryCode, phone, industry, token) =>
    api.post(
      'auth/sign-up-202011',
      {username, password, company, fullname, country, countryCode, phone, industry, token}
    )

  const forgotPassword = (username) => api.post('auth/forgot-password', {username})

  const resetPassword = (username, password, code, id) =>
    api.post('auth/reset-password', {username, code, password, id})

  const activateUser = (username, forename, surname, password, code, id) =>
    api.post(
      'auth/activate-user',
      {
        username,
        forename,
        surname,
        password,
        code,
        id,
      }
    )

  const activateUserWithJwt = (username, forename, surname, password, token) => {
    const api = createPublicAPI(baseURL, token)
    return api.post('jwt/activate-user', {token, username, forename, surname, password})
  }

  const logout = () => api.post('auth/logout')

  const finaliseAccount = (params, fields, uuid) => callOne(serviceCall('ACCOUNT_FINALISE', params, fields, uuid))

  const config = {
    load: () => api.get('config/load'),
    serviceHealth: () => api.get('config/service-health'),
  }

  const stateFields = {
    data: {
      accountingDepartments: true,
      billingDetails: true,
      businessTypes: true,
      campaignGroups: true,
      campaigns: true,
      companyGroups: true,
      contactDataFields: true,
      contactListCategories: true,
      contactLists: true,
      contactMethods: true,
      contactRoles: true,
      countries: true,
      currencies: true,
      deliveryMethods: true,
      deliveryReturnReasons: true,
      features: true,
      globalLayouts: true,
      homeContacts: true,
      individualDataFields: true,
      locationTypes: true,
      nominalCodes: true,
      opportunityStages: true,
      orderDataFields: true,
      orderItemDataFields: true,
      orderReasons: true,
      orderTypes: true,
      packTypes: true,
      partTemplates: true,
      permissions: true,
      prefixTypes: true,
      priceLists: true,
      pricingGroups: true,
      pricingTemplates: true,
      printTemplates: true,
      printTemplateGroups: true,
      productionReasons: true,
      products: true,
      purchaseOrderDataFields: true,
      purchaseOrderItemDataFields: true,
      referralFeeValues: true,
      referralTypes: true,
      sendAddresses: true,
      taxCodes: true,
      teamMembers: true,
      teams: true,
      textTemplates: true,
    },
    settingsContext: {
      tags: true,
      tagCategories: true,
    },
    productionContext: {
      jobBoards: {
        productionDepartments: true,
        productionWorkCentres: true,
      },
    },
    integration: {
      xero: true,
      quickBooks: true,
      hubspot: true,
      mailchimp: true,
      shopify: true,
    },
  }

  const state = {
    load: () => api.post('state/load', {fields: stateFields}),
    save: (state) => api.post('state/save', state),
  }

  const contact = {
    /**
     * @params - An object of params to restrict the returned list of objects :
     * id, companyId
     *
     * @fields - the values you want returned, includes dependencies -
     * leave null for defaults:
     * id,forename,surname,companyId,type,locationId,companyType,campaignId,
     * initialCampaignId,phone,mobile,email,jobTitle,departments,homePhone,
     * extension,initials,salutation,notes,accountManagerId,createdDate,
     * modifiedDate,createdBy,modifiedBy,internalGuid,syncId,urlFacebook,
     * urlTwitter,urlLinkedIn,urlYouTube,emailStatus,smsStatus,fax,pager,email2,
     * contactPrefs,linkedContactId,guid
     *
     * dependencies - may be objects containing their own field definitions -
     * company,location,campaign,initialCampaign,accountManager,linkedContact,
     * quotes,invoices,opportunities
     *
     * e.g. params = { id : 1234, companyId: { 123, 321 } }
     * fields = { id: true, forename: true, surname: true, company: true, quotes: { ref: true } }
     * you can use addProperties to generate a simple non-nested object addProperties('id,forename,surname,company')
     *
     * RETURN
     * response: {
     *   ok: bool,           -- did we talk to the server?
     *   problem: string,    -- if not, what was the issue?
     *   status: number,     -- the HTTP status code
     *   data: [           -- an array of responses for each requested service call
     *     { -- response for first request
     *       success: bool,      -- was the request successful?
     *       message: string,    -- specific feedback message if unsuccessful
     *       errorCode: number,  -- code denoting specific error, if applicable
     *       count: null|number, -- total count of un-filtered records
     *       result: [],         -- array of JSON-encoded records
     *     },
     *     ...
     *  ]
     * }
     */
    getOne: (id, fields, params, uuid) => getOne(contact, id, fields, params, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('CONTACT_READ', params, fields, uuid)),
    dormant: (contactId, replaceId, uuid) =>
      callOne(serviceCall('CONTACT_DORMANT', {'id': contactId, replaceId}, {}, uuid)),
    live: (contactId, uuid) => callOne(serviceCall('CONTACT_LIVE', {'id': contactId}, {}, uuid)),
    delete: (contactId, replaceId, uuid) => callOne(serviceCall('CONTACT_DELETE', {contactId, replaceId}, {}, uuid)),
    pin: (contactId, uuid) => callOne(serviceCall('CONTACT_PIN', {'contactId': contactId}, uuid)),
    unpin: (contactId, uuid) => callOne(serviceCall('CONTACT_UNPIN', {'contactId': contactId}, uuid)),
    add: (params, fields, uuid) => callOne(serviceCall('CONTACT_ADD', params, fields, uuid)),
    edit: (params, fields, uuid) => callOne(serviceCall('CONTACT_UPDATE', params, fields, uuid)),
    summary: (params, fields, uuid) => callOne(serviceCall('CONTACT_SUMMARY_READ', params, fields, uuid)),
    subscribe: (params, fields, uuid) => callOne(serviceCall('CONTACT_SUBSCRIBE', params, fields, uuid)),
  }

  const contactsAndCompanies = {
    get: (params, fields, uuid) =>
      mapSingleResponse(callOne(serviceCall('CONTACT_COMPANY_READ', params, fields, uuid))),
  }

  const contactImportJob = {
    uploadFile: (formData, config) => api.post('binary/import-contacts', formData, config),
    get: (params, fields, uuid) => callOne(serviceCall('CONTACT_IMPORT_JOB_READ', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('CONTACT_IMPORT_JOB_UPDATE', params, fields, uuid)),
    validate: (params, fields, uuid) => callOne(serviceCall('CONTACT_IMPORT_JOB_VALIDATE', params, fields, uuid)),
    preCommit: (params, fields, uuid) => callOne(serviceCall('CONTACT_IMPORT_JOB_PRECOMMIT', params, fields, uuid)),
    commit: (params, fields, uuid) => callOne(serviceCall('CONTACT_IMPORT_JOB_COMMIT', params, fields, uuid)),
    reset: (params, fields, uuid) => callOne(serviceCall('CONTACT_IMPORT_JOB_RESET', params, fields, uuid)),
  }

  const contactImportData = {
    get: (params, fields, uuid) => callOne(serviceCall('CONTACT_IMPORT_DATA_READ', params, fields, uuid)),
  }

  const trustedDeviceData = {
    get: (params, fields, uuid) => callOne(serviceCall('TRUSTED_DEVICES_READ', params, fields, uuid)),
    delete: (params, fields, uuid) => callOne(serviceCall('TRUSTED_DEVICES_DELETE', params, fields, uuid)),
  }


  const contactList = {
    getOne: (id, fields, uuid) => getOne(contactList, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('CONTACT_LIST_READ', params, fields, uuid)),
    add: (contactList, fields, uuid) => callOne(serviceCall('CONTACT_LIST_ADD', {contactList}, fields, uuid)),
    edit: (contactList, fields, uuid) => callOne(serviceCall('CONTACT_LIST_UPDATE', {contactList}, fields, uuid)),
    delete: (id, uuid) => callOne(serviceCall('CONTACT_LIST_DELETE', {id}, null, uuid)),
    requery: (id, uuid) => callOne(serviceCall('CONTACT_SUMMARY_READ', {contactListId: id}, null, uuid)),
    requeryList: (contactList, uuid) => callOne(serviceCall('CONTACT_SUMMARY_READ', {contactList}, null, uuid)),
    dormantContacts: (contactListId, uuid) => callOne(serviceCall('CONTACT_DORMANT', {contactListId}, {}, uuid)),
    allocate: (params, fields, uuid) => callOne(serviceCall('CONTACT_LIST_ALLOCATE', params, fields, uuid)),
  }

  const contactListCategory = {
    getOne: (id, fields, uuid) => getOne(contactListCategory, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('CONTACT_LIST_CATEGORY_READ', params, fields, uuid)),
    add: (category, uuid) => callOne(serviceCall('CONTACT_LIST_CATEGORY_ADD', {category}, null, uuid)),
    edit: (category, uuid) => callOne(serviceCall('CONTACT_LIST_CATEGORY_UPDATE', {category}, null, uuid)),
    delete: (id, adoptId, uuid) => callOne(serviceCall('CONTACT_LIST_CATEGORY_DELETE', {id, adoptId}, null, uuid)),
  }

  const company = {
    getOne: (id, fields, params, uuid) => getOne(company, id, fields, params, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('COMPANY_READ', params, fields, uuid)),
    dormant: (companyId, uuid) => callOne(serviceCall('COMPANY_DORMANT', {'id': companyId}, {}, uuid)),
    live: (companyId, uuid) => callOne(serviceCall('COMPANY_LIVE', {'id': companyId}, {}, uuid)),
    delete: (companyId, replacementId = null, uuid) =>
      callOne(serviceCall('COMPANY_DELETE', {'id': companyId, 'replacementId': replacementId}, {}, uuid)),
    add: (params, fields, uuid) => callOne(serviceCall('CONTACT_ADD', params, fields, uuid)),
    edit: (params, fields, uuid) => callOne(serviceCall('COMPANY_UPDATE', params, fields, uuid)),
    uploadImage: (companyId, formData, config) =>
      api.post(`binary/upload-company-image/${companyId}`, formData, config),
    removeImage: (companyId) => api.post(`binary/delete-company-image/${companyId}`, {}, {}),
  }

  const companyGroups = {
    getOne: (id, fields, uuid) => getOne(companyGroups, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('COMPANY_GROUP_READ', params, fields, uuid)),
  }

  const deliveryMethods = {
    getOne: (id, fields, uuid) => getOne(pricingTemplates, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('DELIVERY_METHOD_READ', params, fields, uuid)),
    add: (params, fields, uuid) => callOne(serviceCall('DELIVERY_METHOD_ADD', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('DELIVERY_METHOD_UPDATE', params, fields, uuid)),
    delete: (params, fields, uuid) => callOne(serviceCall('DELIVERY_METHOD_DELETE', params, fields, uuid)),
  }

  const deliveryNote = {
    getOne: (id, fields, uuid) => getOne(deliveryNote, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('DELIVERY_NOTE_READ', params, fields, uuid)),
    add: (params, fields, uuid) => callOne(serviceCall('DELIVERY_NOTE_ADD', params, fields, uuid)),
    edit: (params, fields, uuid) => callOne(serviceCall('DELIVERY_NOTE_UPDATE', params, fields, uuid)),
    delete: (params, fields, uuid) => callOne(serviceCall('DELIVERY_NOTE_DELETE', params, fields, uuid)),
    sendEmail: (formData) => api.post('email/delivery-note-send-email', formData, emailMultipartConfig),
  }

  const deliveryNoteItem = {
    getOne: (id, fields, uuid) => getOne(quote, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('DELIVERY_NOTE_ITEM_READ', params, fields, uuid)),
  }

  const deliveryReasons = {
    get: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('DELIVERY_REASON_READ', params, fields, uuid)),
    add: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('DELIVERY_REASON_ADD', params, fields, uuid)),
    update: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('DELIVERY_REASON_UPDATE', params, fields, uuid)),
    delete: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('DELIVERY_REASON_DELETE', params, fields, uuid)),
  }

  const delivery = {
    add: (params, fields = {}, uuid) => callOne(serviceCall('DELIVERY_ADD', params, fields, uuid)),
    getOne: (id, fields, uuid) => getOne(delivery, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('DELIVERY_READ', params, fields, uuid)),
    move: (params, fields, uuid) => callOne(serviceCall('DELIVERY_MOVE', params, fields, uuid)),
    delete: (params, fields, uuid) => callOne(serviceCall('DELIVERY_DELETE', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('DELIVERY_UPDATE', params, fields, uuid)),
    getSummary: (params = {}, fields = {}, uuid) =>
      callOne(serviceCall('DELIVERY_SUMMARY_READ', params, fields, uuid)),
  }

  const fields = {
    get: (params, fields, uuid) => callOne(serviceCall('FIELDS_READ', params, fields, uuid)),
  }

  const fieldsOptions = {
    get: (params, fields, uuid) => callOne(serviceCall('FIELDS_OPTIONS_READ', params, fields, uuid)),
  }

  const emailing = {
    contactListPost: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('EMAILING_CONTACT_LIST_POST', params, fields, uuid)),
    contactPost: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('EMAILING_CONTACT_POST', params, fields, uuid)),
    opportunityPost: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('EMAILING_OPPORTUNITY_POST', params, fields, uuid)),
  }

  const taxCode = {
    get: (params, fields, uuid) => callOne(serviceCall('TAX_CODE_READ', params, fields, uuid)),
    update: (params, fields = {}, uuid) => callOne(serviceCall('TAX_CODE_UPDATE', params, fields, uuid)),
    setDefault: (params, fields = {}, uuid) => callOne(serviceCall('TAX_CODE_SET_DEFAULT', params, fields, uuid)),
  }

  const countries = {
    get: () => callOne(serviceCall('COUNTRY_READ')),
  }

  const location = {
    getOne: (id, fields, uuid) => getOne(location, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('LOCATION_READ', params, fields, uuid)),
    add: (params, fields, uuid) => callOne(serviceCall('LOCATION_ADD', params, fields, uuid)),
    edit: (params, fields, uuid) => callOne(serviceCall('LOCATION_UPDATE', params, fields, uuid)),
    delete: (locationId, uuid) => callOne(serviceCall('LOCATION_DELETE', {'locationId': locationId}, {}, uuid)),
  }

  const activity = {
    getOne: (id, fields, uuid) => getOne(activity, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('ACTIVITY_READ', params, fields, uuid)),
    add: (params = {}, fields = {}, uuid) => callOne(serviceCall('ACTIVITY_ADD', params, fields, uuid)),
    update: (params = {}, fields = {}, uuid) => callOne(serviceCall('ACTIVITY_UPDATE', params, fields, uuid)),
    updateAdd: (params = {}, fields = {}, uuid) =>
      callOne(serviceCall('ACTIVITY_UPDATE_ADD', params, fields, uuid)),
    updateDates: (params = {}, fields = {}, uuid) =>
      callOne(serviceCall('ACTIVITY_UPDATE_DATES', params, fields, uuid)),
    getSummary: (params = {}, fields = {}, uuid) =>
      callOne(serviceCall('ACTIVITY_SUMMARY_READ', params, fields, uuid)),
    delete: (params = {}, fields = {}, uuid) => callOne(serviceCall('ACTIVITY_DELETE', params, fields, uuid)),
    updateUpdate: (params = {}, fields = {}, uuid) =>
      callOne(serviceCall('ACTIVITY_UPDATE_UPDATE', params, fields, uuid)),
  }

  const activityTypes = {
    getOne: (id, fields = {default: true}, uuid) => getOne(activityTypes, id, fields, uuid),
    get: (params, fields = {default: true}, uuid) =>
      callOne(serviceCall('ACTIVITY_TYPE_READ', params, fields, uuid)),
    add: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('ACTIVITY_TYPE_ADD', params, fields, uuid)),
    update: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('ACTIVITY_TYPE_UPDATE', params, fields, uuid)),
    delete: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('ACTIVITY_TYPE_DELETE', params, fields, uuid)),
  }

  const activityResourceTypes = {
    get: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('ACTIVITY_RESOURCE_TYPE_READ', params, fields, uuid)),
    add: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('ACTIVITY_RESOURCE_TYPE_ADD', params, fields, uuid)),
    update: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('ACTIVITY_RESOURCE_TYPE_UPDATE', params, fields, uuid)),
    delete: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('ACTIVITY_RESOURCE_TYPE_DELETE', params, fields, uuid)),
  }

  const resource = {
    get: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('RESOURCE_READ', params, fields, uuid)),
    add: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('RESOURCE_ADD', params, fields, uuid)),
    update: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('RESOURCE_UPDATE', params, fields, uuid)),
  }

  const activityResources = {
    get: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('ACTIVITY_RESOURCE_READ', params, fields, uuid)),
    add: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('ACTIVITY_RESOURCE_ADD', params, fields, uuid)),
    update: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('ACTIVITY_RESOURCE_UPDATE', params, fields, uuid)),
    delete: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('ACTIVITY_RESOURCE_DELETE', params, fields, uuid)),
  }

  const activityReasons = {
    get: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('ACTIVITY_REASON_READ', params, fields, uuid)),
    add: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('ACTIVITY_REASON_ADD', params, fields, uuid)),
    update: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('ACTIVITY_REASON_UPDATE', params, fields, uuid)),
    delete: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('ACTIVITY_REASON_DELETE', params, fields, uuid)),
  }

  const history = {
    getOne: (id, fields, uuid) => getOne(history, id, fields, uuid),
    getStats: (params, fields, uuid) => callOne(serviceCall('HISTORY_STAT_READ', params, fields, uuid)),
  }

  const documents = {
    authorise: () => callOne(serviceCall('GOOGLE_DRIVE_AUTHENTICATE')),
    getCompanyDocumentsFolder: (companyId) => api.get('google-drive/company/get/' + companyId),
    getQuoteDocumentsFolder: (quoteId) => api.get('google-drive/order/get/' + quoteId),
    getPurchaseOrderFolder: (purchaseOrderId) => api.get('google-drive/purchase-order/get/' + purchaseOrderId),
    get: (params) => api.get('google-drive/directory/list/' + params.folderId),
    getDocuments: (folderId) => api.get('google-drive/directory/list/' + folderId),
    uploadDocuments: (folderId, formData, config) =>
      api.post('google-drive/file/upload-to/' + folderId, formData, config),
    delete: (documentIds) => api.post('google-drive/file/delete', documentIds),
    isAuthenticated: () => api.get('google-drive/is-authenticated'),
    deauthenticate: () => api.get('google-drive/deauthenticate'),
    setBaseFolder: (folderId) => api.get('google-drive/directory/set-base/' + folderId),
    readFolder: (folderId) => api.get('google-drive/directory/read/' + folderId),
  }

  const documentFolderTemplates = {
    get: (params, fields, uuid) => callOne(serviceCall('DOCUMENT_FOLDER_TEMPLATE_READ', params, fields, uuid)),
    add: (params, fields, uuid) => callOne(serviceCall('DOCUMENT_FOLDER_TEMPLATE_ADD', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('DOCUMENT_FOLDER_TEMPLATE_UPDATE', params, fields, uuid)),
    delete: (params, fields, uuid) => callOne(serviceCall('DOCUMENT_FOLDER_TEMPLATE_DELETE', params, fields, uuid)),
  }

  const oneDrive = {
    authorise: () => callOne(serviceCall('ONE_DRIVE_AUTHENTICATE')),
    disable: () => callOne(serviceCall('ONE_DRIVE_DISABLE')),
    status: () => callOne(serviceCall('ONE_DRIVE_STATUS')),
    uploadFile: (url, file, headers) => api.put(url, file, {headers}),
  }

  const sharePoint = {
    authorise: () => callOne(serviceCall('SHAREPOINT_AUTHENTICATE')),
    disable: () => callOne(serviceCall('SHAREPOINT_DISABLE')),
    status: () => callOne(serviceCall('SHAREPOINT_STATUS')),
  }

  const xero = {
    authorise: () => callOne(serviceCall('XERO_AUTHENTICATE')),
    enabled: () => callOne(serviceCall('XERO_ENABLED')),
    disable: () => callOne(serviceCall('XERO_DISABLE')),
    status: () => callOne(serviceCall('XERO_STATUS')),
    updateAccountData: () => callOne(serviceCall('XERO_UPDATE_ACCOUNT_DATA')),
  }

  const quickBooks = {
    authorise: () => callOne(serviceCall('QUICK_BOOKS_AUTHENTICATE')),
    enabled: () => callOne(serviceCall('QUICK_BOOKS_ENABLED')),
    disable: () => callOne(serviceCall('QUICK_BOOKS_DISABLE')),
    status: () => callOne(serviceCall('QUICK_BOOKS_STATUS')),
    updateAccountData: () => callOne(serviceCall('QUICK_BOOKS_UPDATE_ACCOUNT_DATA')),
  }

  const sage50 = {
    get: () => callOne(serviceCall('ACCOUNTING_SAGE50_PROVIDER_READ')),
    update: (params) => callOne(serviceCall('ACCOUNTING_SAGE50_PROVIDER_UPDATE', params)),
    enableSage50: (params) => callOne(serviceCall('ACCOUNTING_SAGE50_PROVIDER_ENABLE', params)),
    disableSage50: (params) => callOne(serviceCall('ACCOUNTING_SAGE50_PROVIDER_DISABLE', params)),
    updateData: () => callOne(serviceCall('ACCOUNTING_SAGE50_DATA_UPDATE')),
  }

  const hubspot = {
    authorise: () => callOne(serviceCall('HUBSPOT_AUTHENTICATE')),
    enabled: () => callOne(serviceCall('HUBSPOT_ENABLED')),
    disable: () => callOne(serviceCall('HUBSPOT_DISABLE')),
    status: () => callOne(serviceCall('HUBSPOT_STATUS')),
    lists: (params, fields, uuid) => callOne(serviceCall('HUBSPOT_LIST_READ', params, fields, uuid)),
    contactProperties: (params, fields, uuid) => callOne(serviceCall('HUBSPOT_CONTACT_PROPERTIES_READ', params, fields, uuid)),
    updateData: () => callOne(serviceCall('HUBSPOT_UPDATE_DATA')),
  }

  const mailchimp = {
    authorise: () => callOne(serviceCall('MAILCHIMP_AUTHENTICATE')),
    enabled: () => callOne(serviceCall('MAILCHIMP_ENABLED')),
    disable: () => callOne(serviceCall('MAILCHIMP_DISABLE')),
    status: () => callOne(serviceCall('MAILCHIMP_STATUS')),
    lists: (params, fields, uuid) => callOne(serviceCall('MAILCHIMP_LIST_READ', params, fields, uuid)),
    mergeFields: (params, fields, uuid) => callOne(serviceCall('MAILCHIMP_MERGE_FIELDS_READ', params, fields, uuid)),
  }

  const opportunity = {
    getOne: (id, fields, uuid) => getOne(opportunity, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('OPPORTUNITY_READ', params, fields, uuid)),
    add: (params, fields, uuid) => mapSingleResponse(callOne(serviceCall('OPPORTUNITY_ADD', params, fields, uuid))),
    update: (params, fields, uuid) => mapSingleResponse(callOne(serviceCall('OPPORTUNITY_UPDATE', params, fields, uuid))),
    getSuccessStats: (params, fields, uuid) => callOne(serviceCall('OPPORTUNITY_SUCCESS_STAT_READ', params, fields, uuid)),
  }

  const opportunityHistory = {
    getOne: (id, fields, uuid) => getOne(opportunity, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('OPPORTUNITY_HISTORY_READ', params, fields, uuid)),
  }

  const opportunityStage = {
    getOne: (id, fields, uuid) => getOne(opportunity, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('OPPORTUNITY_STAGE_READ', params, fields, uuid)),
    add: (params, fields, uuid) => callOne(serviceCall('OPPORTUNITY_STAGE_ADD', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('OPPORTUNITY_STAGE_UPDATE', params, fields, uuid)),
    delete: (params, fields, uuid) => callOne(serviceCall('OPPORTUNITY_STAGE_DELETE', params, fields, uuid)),
  }

  const opportunityReason = {
    getOne: (id, fields, uuid) => getOne(opportunity, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('OPPORTUNITY_REASON_READ', params, fields, uuid)),
    add: (params, fields, uuid) => callOne(serviceCall('OPPORTUNITY_REASON_ADD', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('OPPORTUNITY_REASON_UPDATE', params, fields, uuid)),
    delete: (params, fields, uuid) => callOne(serviceCall('OPPORTUNITY_REASON_DELETE', params, fields, uuid)),
  }

  const subscriptions = {
    add: (params, fields, uuid) => callOne(serviceCall('SUBSCRIPTION_ADD', params, fields, uuid), {timeout: 60000}),
    update: (params, fields, uuid) =>
      callOne(serviceCall('SUBSCRIPTION_UPDATE', params, fields, uuid), {timeout: 60000}),
    cancel: (params, fields, uuid) => callOne(serviceCall('SUBSCRIPTION_CANCEL', params, fields, uuid)),
    cancelUpdate: (params, fields, uuid) =>
      callOne(serviceCall('SUBSCRIPTION_UPDATE_CANCEL', params, fields, uuid)),
    updateUsers: (params, fields, uuid) => callOne(serviceCall('SUBSCRIPTION_UPDATE_USERS', params, fields, uuid)),
    getClientSecret: (params, fields, uuid) => callOne(serviceCall('CLIENT_SECRET_READ', params, fields, uuid)),
    getPrice: (params, fields, uuid) => callOne(serviceCall('SUBSCRIPTION_PRICE_READ', params, fields, uuid)),
    getPaymentMethod: (params, fields, uuid) => callOne(serviceCall('PAYMENT_METHOD_READ', params, fields, uuid)),
    updatePaymentMethod: (params, fields, uuid) =>
      callOne(serviceCall('PAYMENT_METHOD_UPDATE', params, fields, uuid)),
    getUpcomingInvoice: (params, fields, uuid) =>
      callOne(serviceCall('SUBSCRIPTION_UPCOMING_INVOICE_READ', params, fields, uuid)),
    estimateInvoice: (params, fields, uuid) =>
      callOne(serviceCall('SUBSCRIPTION_INVOICE_READ', params, fields, uuid)),
    getCancellationDate: (params, fields, uuid) =>
      callOne(serviceCall('SUBSCRIPTION_CANCEL_DATE_READ', params, fields, uuid)),
  }

  const quote = {
    getOne: (id, fields, uuid) => getOne(quote, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('ORDER_READ', params, fields, uuid)),
    add: (params, fields, uuid) => mapSingleResponse(callOne(serviceCall('ORDER_ADD', params, fields, uuid))),
    update: (params, fields, uuid) => mapSingleResponse(callOne(serviceCall('ORDER_UPDATE', params, fields, uuid))),
    changeStatus: (params, fields, uuid) =>
      mapSingleResponse(callOne(serviceCall('ORDER_CHANGE_STATUS', params, fields, uuid))),
    markDelivered: (params, fields, uuid) =>
      mapSingleResponse(callOne(serviceCall('ORDER_MARK_DELIVERED', params, fields, uuid))),
    sendEmail: (formData) => api.post('email/order-send-email', formData, emailMultipartConfig),
    sendApprovalEmail: (formData) => api.post('email/order-send-approval-email', formData, emailMultipartConfig),
    updateOrderHistory: (params) => callOne(serviceCall('ORDER_HISTORY_UPDATE', params)),
    sendAcceptance: (formData) => api.post('email/order-send-acceptance-email', formData, emailMultipartConfig),
    summary: (params, fields, uuid) => callOne(serviceCall('ORDER_SUMMARY_READ', params, fields, uuid)),
    summaryInfo: (params, fields, uuid) => callOne(serviceCall('ORDER_SUMMARY_INFO_READ', params, fields, uuid)),
    removeFromJobBoard: (params, fields, uuid) =>
      callOne(serviceCall('ORDER_REMOVE_FROM_JOB_BOARD', params, fields, uuid)),

    acceptanceUrl: (orderId, printTemplateId, uuid) => callOne(serviceCall(
      'ORDER_ACCEPTANCE_URL_READ',
      {order: orderId, template: printTemplateId},
      {},
      uuid)),
    closeOrReject: (params, fields, uuid) => callOne(serviceCall('ORDER_CLOSE_OR_REJECT', params, fields, uuid)),
  }

  const quoteHistory = {
    getOne: (id, fields, uuid) => getOne(quoteHistory, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('ORDER_HISTORY_READ', params, fields, uuid)),
  }

  const quoteAmendments = {
    getOne: (id, fields, uuid) => getOne(quote, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('ORDER_AMENDMENT_READ', params, fields, uuid)),
  }

  const quoteStats = {
    getOne: (params, fields, uuid) => getOneWithParams(quoteStats, params, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('ORDER_STAT_READ', params, fields, uuid)),
  }

  const quoteItem = {
    getOne: (id, fields, uuid) => getOne(quoteItem, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('ORDER_ITEM_READ', params, fields, uuid)),
  }

  const quoteRevision = {
    add: (params, fields, uuid) => mapSingleResponse(callOne(serviceCall('ORDER_ADD_REVISION', params, fields, uuid))),
  }

  const invoice = {
    getOne: (id, fields, uuid) => getOne(invoice, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('INVOICE_READ', params, fields, uuid)),
    add: (params, fields, uuid) => mapSingleResponse(callOne(serviceCall('INVOICE_ADD', params, fields, uuid))),
    createBulk: (params, fields, uuid) => callOne(serviceCall('BULK_INVOICE_ADD', params, fields, uuid)),
    update: (params, fields, uuid) => mapSingleResponse(callOne(serviceCall('INVOICE_UPDATE', params, fields, uuid))),
    updateFromJob: (params, fields, uuid) =>
      mapSingleResponse(callOne(serviceCall('INVOICE_UPDATE_FROM_JOB', params, fields, uuid))),
    sendEmail: (formData) => api.post('email/invoice-send-email', formData, emailMultipartConfig),
    addHistory: (params) => callOne(serviceCall('INVOICE_HISTORY_ADD', params)),
    markPosted: (params, fields, uuid) =>
      mapSingleResponse(callOne(serviceCall('INVOICE_MARK_POSTED', params, fields, uuid))),
    unmarkPosted: (params, fields, uuid) =>
      mapSingleResponse(callOne(serviceCall('INVOICE_UNMARK_POSTED', params, fields, uuid))),
    summary: (params, fields, uuid) => callOne(serviceCall('INVOICE_SUMMARY_READ', params, fields, uuid)),
  }

  const invoiceHistory = {
    getOne: (id, fields, uuid) => getOne(invoiceHistory, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('INVOICE_HISTORY_READ', params, fields, uuid)),
  }

  const purchaseInvoice = {
    getOne: (id, fields, uuid) => getOne(purchaseInvoice, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('PURCHASE_INVOICE_READ', params, fields, uuid)),
    markPosted: (params, fields, uuid) =>
      mapSingleResponse(callOne(serviceCall('PURCHASE_INVOICE_MARK_POSTED', params, fields, uuid))),
    unmarkPosted: (params, fields, uuid) =>
      mapSingleResponse(callOne(serviceCall('PURCHASE_INVOICE_UNMARK_POSTED', params, fields, uuid))),
    add: (params, fields, uuid) =>
      mapSingleResponse(callOne(serviceCall('PURCHASE_INVOICE_ADD', params, fields, uuid))),
    update: (params, fields, uuid) =>
      mapSingleResponse(callOne(serviceCall('PURCHASE_INVOICE_UPDATE', params, fields, uuid))),
    updateFromJob: (params, fields, uuid) =>
      mapSingleResponse(callOne(serviceCall('PURCHASE_INVOICE_UPDATE_FROM_JOB', params, fields, uuid))),
  }

  const purchaseInvoiceItem = {
    getOne: (id, fields, uuid) => getOne(purchaseInvoiceItem, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('PURCHASE_INVOICE_ITEM_READ', params, fields, uuid)),
  }

  const purchaseOrder = {
    getOne: (id, fields, uuid) => getOne(purchaseOrder, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('PURCHASE_ORDER_READ', params, fields, uuid)),
    add: (params, fields, uuid) =>
      mapSingleResponse(callOne(serviceCall('PURCHASE_ORDER_ADD', params, fields, uuid))),
    update: (params, fields, uuid) =>
      mapSingleResponse(callOne(serviceCall('PURCHASE_ORDER_UPDATE', params, fields, uuid))),
    changeStatus: (params, fields, uuid) =>
      mapSingleResponse(callOne(serviceCall('PURCHASE_ORDER_CHANGE_STATUS', params, fields, uuid))),
    sendEmail: (formData) => api.post('email/purchase-order-send-email', formData, emailMultipartConfig),
    sendAcceptance: (formData) => api.post('email/purchase-order-send-acceptance-email', formData, emailMultipartConfig),
    copy: (params, fields, uuid) =>
      mapSingleResponse(callOne(serviceCall('PURCHASE_ORDER_COPY', params, fields, uuid))),
    accept: (params, fields, uuid) =>
      mapSingleResponse(callOne(serviceCall('PURCHASE_ORDER_ACCEPT', params, fields, uuid))),
    markInvoiced: (params, fields, uuid) =>
      callOne(serviceCall('PURCHASE_ORDER_MARK_INVOICED', params, fields, uuid)),
    markReceived: (params, fields, uuid) =>
      callOne(serviceCall('PURCHASE_ORDER_MARK_RECEIVED', params, fields, uuid)),
    acceptanceUrl: (purchaseOrderId, printTemplateId, uuid) => callOne(serviceCall(
      'PURCHASE_ORDER_ACCEPTANCE_URL_READ',
      {purchaseOrder: purchaseOrderId, template: printTemplateId},
      {},
      uuid
    )),
  }

  const purchaseOrderItem = {
    getOne: (id, fields, uuid) => getOne(purchaseOrderItem, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('PURCHASE_ORDER_ITEM_READ', params, fields, uuid)),
  }

  const purchaseOrderHistory = {
    getOne: (id, fields, uuid) => getOne(purchaseOrderHistory, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('PURCHASE_ORDER_HISTORY_READ', params, fields, uuid)),
    add: (params) => callOne(serviceCall('PURCHASE_ORDER_HISTORY_ADD', params)),
  }

  const accounting = {
    invoicePostAsync: (id) => callOne(serviceCall('ACCOUNTING_INVOICE_POST_ASYNC', id)),
    purchaseInvoicePostAsync: (id) => callOne(serviceCall('ACCOUNTING_PURCHASE_INVOICE_POST_ASYNC', id)),
    getAccounts: (params) => callOne(serviceCall('ACCOUNTING_ACCOUNT_READ', params)),
    linkAccount: (params) => callOne(serviceCall('ACCOUNTING_ACCOUNT_LINK', params)),
    addAccount: (params) => callOne(serviceCall('ACCOUNTING_ACCOUNT_ADD', params)),
    getTaxCodes: () => callOne(serviceCall('ACCOUNTING_TAX_CODE_READ')),
    updateEmails: (params) => callOne(serviceCall('ACCOUNTING_CONTACT_EMAIL_UPDATE', params)),
    getCurrentAccountingProvider: () => callOne(serviceCall('ACCOUNTING_PROVIDER_CURRENT')),
  }

  const jobCards = {
    getOne: (id, fields, uuid) => getOne(teams, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('JOB_CARD_READ', params, fields, uuid)),
    add: (params, fields, uuid) => mapSingleResponse(callOne(serviceCall('JOB_CARD_ADD', params, fields, uuid))),
    addItems: (params, fields, uuid) => callOne(serviceCall('JOB_CARDS_ADD', params, fields, uuid)),
    update: (params, fields, uuid) => mapSingleResponse(callOne(serviceCall('JOB_CARD_UPDATE', params, fields, uuid))),
  }

  const invoiceItem = {
    getOne: (id, fields, uuid) => getOne(invoiceItem, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('INVOICE_ITEM_READ', params, fields, uuid)),
  }

  const campaigns = {
    getOne: (id, fields, uuid) => getOne(campaigns, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('CAMPAIGN_READ', params, fields, uuid)),
  }

  const campaignGroups = {
    getOne: (id, fields, uuid) => getOne(campaignGroups, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('CAMPAIGN_GROUP_READ', params, fields, uuid)),
  }

  const notifications = {
    getOne: (id, fields, uuid) => getOne(notifications, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('NOTIFICATION_READ', params, fields, uuid)),
    snooze: (params, fields, uuid) => callOne(serviceCall('NOTIFICATION_SNOOZE', params, fields, uuid)),
    dismiss: (params, fields, uuid) => callOne(serviceCall('NOTIFICATION_DISMISS', params, fields, uuid)),
    dismissAll: (params, fields, uuid) => callOne(serviceCall('NOTIFICATION_DISMISS_ALL', params, fields, uuid)),
  }

  const userFields = {
    getOne: (id, fields, uuid) => getOne(tag, id, fields, uuid),
    get: (params, fields = {default: true}, uuid) => callOne(serviceCall('TAG_READ', params, fields, uuid)),
    update: (params, fields = {default: true}, uuid) => callOne(serviceCall('TAG_UPDATE', params, fields, uuid)),
    add: (params, fields = {default: true}, uuid) => callOne(serviceCall('TAG_ADD', params, fields, uuid)),
    delete: (params, fields = {default: true}, uuid) => callOne(serviceCall('TAG_DELETE', params, fields, uuid)),
    reorder: (params, fields = {default: true}, uuid) =>
      callOne(serviceCall('TAG_UPDATE_ORDER', params, fields, uuid)),
    move: (params, fields = {default: true}, uuid) =>
      callOne(serviceCall('TAG_UPDATE_CATEGORY', params, fields, uuid)),
  }

  const userFieldCategories = {
    get: (params, fields = {default: true}, uuid) =>
      callOne(serviceCall('TAG_CATEGORY_READ', params, fields, uuid)),
    update: (params, fields = {default: true}, uuid) =>
      callOne(serviceCall('TAG_CATEGORY_UPDATE', params, fields, uuid)),
    add: (params, fields = {default: true}, uuid) =>
      callOne(serviceCall('TAG_CATEGORY_ADD', params, fields, uuid)),
    delete: (params, fields = {default: true}, uuid) =>
      callOne(serviceCall('TAG_CATEGORY_DELETE', params, fields, uuid)),
    reorder: (params, fields = {default: true}, uuid) =>
      callOne(serviceCall('TAG_CATEGORY_UPDATE_ORDER', params, fields, uuid)),
  }

  const products = {
    getOne: (id, fields, uuid) => getOne(products, id, fields, uuid),
    get: (params, fields = {default: true}, uuid) => callOne(serviceCall('PRODUCT_READ', params, fields, uuid)),
    update: (params, fields = {default: true}, uuid) => callOne(serviceCall('PRODUCT_UPDATE', params, fields, uuid)),
    add: (params, fields = {default: true}, uuid) => callOne(serviceCall('PRODUCT_ADD', params, fields, uuid)),
    delete: (params, fields = {default: true}, uuid) => callOne(serviceCall('PRODUCT_DELETE', params, fields, uuid)),
  }

  const currency = {
    getOne: (id, fields, uuid) => getOne(currency, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('CURRENCY_READ', params, fields, uuid)),
  }

  const businessTypes = {
    get: (params, fields, uuid) => callOne(serviceCall('BUSINESS_TYPE_READ', params, fields, uuid)),
    add: (params, fields = {}, uuid) => callOne(serviceCall('BUSINESS_TYPE_ADD', params, fields, uuid)),
    update: (params, fields = {}, uuid) => callOne(serviceCall('BUSINESS_TYPE_UPDATE', params, fields, uuid)),
    delete: (params, fields = {}, uuid) => callOne(serviceCall('BUSINESS_TYPE_DELETE', params, fields, uuid)),
  }

  const textTemplates = {
    getOne: (id, fields, uuid) => getOne(textTemplates, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('TEXT_TEMPLATE_READ', params, fields, uuid)),
    add: (params, fields = {}, uuid) => callOne(serviceCall('TEXT_TEMPLATE_ADD', params, fields, uuid)),
    update: (params, fields = {}, uuid) => callOne(serviceCall('TEXT_TEMPLATE_UPDATE', params, fields, uuid)),
    delete: (params, fields = {}, uuid) => callOne(serviceCall('TEXT_TEMPLATE_DELETE', params, fields, uuid)),
  }

  const orderReasons = {
    getOne: (id, fields, uuid) => getOne(orderReasons, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('ORDER_REASON_READ', params, fields, uuid)),
    add: (params, fields = {}, uuid) => callOne(serviceCall('ORDER_REASON_ADD', params, fields, uuid)),
    update: (params, fields = {}, uuid) => callOne(serviceCall('ORDER_REASON_UPDATE', params, fields, uuid)),
    delete: (params, fields = {}, uuid) => callOne(serviceCall('ORDER_REASON_DELETE', params, fields, uuid)),
  }

  const orderTypes = {
    get: (params, fields, uuid) => callOne(serviceCall('ORDER_TYPE_READ', params, fields, uuid)),
    add: (params, fields = {}, uuid) => callOne(serviceCall('ORDER_TYPE_ADD', params, fields, uuid)),
    update: (params, fields = {}, uuid) => callOne(serviceCall('ORDER_TYPE_UPDATE', params, fields, uuid)),
    delete: (params, fields = {}, uuid) => callOne(serviceCall('ORDER_TYPE_DELETE', params, fields, uuid)),
  }

  const nominalCodes = {
    get: (params, fields, uuid) => callOne(serviceCall('NOMINAL_CODE_READ', params, fields, uuid)),
  }

  const layouts = {
    get: (params, fields, uuid) => callOne(serviceCall('LAYOUT_READ', params, fields, uuid)),
    add: (params, fields = {}, uuid) => callOne(serviceCall('LAYOUT_ADD', params, fields, uuid)),
    update: (params, fields = {}, uuid) => callOne(serviceCall('LAYOUT_UPDATE', params, fields, uuid)),
    delete: (params, fields = {}, uuid) => callOne(serviceCall('LAYOUT_DELETE', params, fields, uuid)),
  }


  const packTypes = {
    get: (params, fields, uuid) => callOne(serviceCall('PACK_TYPE_READ', params, fields, uuid)),
    add: (params, fields, uuid) => callOne(serviceCall('PACK_TYPE_ADD', params, fields, uuid)),
    update: (params, fields = {}, uuid) => callOne(serviceCall('PACK_TYPE_UPDATE', params, fields, uuid)),
    delete: (id, uuid) => callOne(serviceCall('PACK_TYPE_DELETE', {id}, null, uuid)),
  }

  const priceListImportJob = {
    uploadFile: (formData, config) => api.post('binary/import-price-lists', formData, config),
    get: (params, fields, uuid) => callOne(serviceCall('PRICE_LIST_IMPORT_JOB_READ', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('PRICE_LIST_IMPORT_JOB_UPDATE', params, fields, uuid)),
    validate: (params, fields, uuid) => callOne(serviceCall('PRICE_LIST_IMPORT_JOB_VALIDATE', params, fields, uuid)),
    preCommit: (params, fields, uuid) => callOne(serviceCall('PRICE_LIST_IMPORT_JOB_PRECOMMIT', params, fields, uuid)),
    commit: (params, fields, uuid) => callOne(serviceCall('PRICE_LIST_IMPORT_JOB_COMMIT', params, fields, uuid)),
    reset: (params, fields, uuid) => callOne(serviceCall('PRICE_LIST_IMPORT_JOB_RESET', params, fields, uuid)),
  }

  const priceListImportData = {
    get: (params, fields, uuid) => callOne(serviceCall('PRICE_LIST_IMPORT_DATA_READ', params, fields, uuid)),
  }

  const parts = {
    get: (params, fields, uuid) => callOne(serviceCall('PART_READ', params, fields, uuid)),
    getPrice: (params, fields, uuid) => callOne(serviceCall('PART_GET_PRICE', params, fields, uuid)),
    getCost: (params, fields, uuid) => callOne(serviceCall('PART_GET_COST', params, fields, uuid)),
    add: (params, fields, uuid) => mapSingleResponse(callOne(serviceCall('PART_ADD', params, fields, uuid))),
    duplicate: (params, fields, uuid) => mapSingleResponse(callOne(serviceCall('PART_DUPLICATE', params, fields, uuid))),
    update: (params, fields, uuid) => callOne(serviceCall('PART_UPDATE', params, fields, uuid)),
    delete: (ids, uuid) => callOne(serviceCall('PART_DELETE', {ids}, null, uuid)),
    checkDelete: (params, uuid) => callOne(serviceCall('PART_DELETE_CHECK', params, null, uuid)),
    partQueryUsage: (id, uuid) => callOne(serviceCall('PART_QUERY_USAGE', {id}, null, uuid)),
    move: (params, fields, uuid) => callOne(serviceCall('PART_MOVE', params, null, uuid)),
    uploadImage: (partId, formData, config) => api.post(`binary/upload-part-image/${partId}`, formData, config),
    removeImage: (partId) => api.post(`binary/delete-part-image/${partId}`, {}, {}),
  }

  const partPacks = {
    getOne: (id, fields, uuid) => getOne(partPacks, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('PART_PACK_READ', params, fields, uuid)),
    add: (params, fields, uuid) => mapSingleResponse(callOne(serviceCall('PART_PACK_ADD', params, fields, uuid))),
    delete: (params, fields = {}, uuid) => callOne(serviceCall('PART_PACK_DELETE', params, fields, uuid)),
  }

  const partPriceBreaks = {
    getOne: (id, fields, uuid) => getOne(partPriceBreaks, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('PART_PRICE_BREAK_READ', params, fields, uuid)),
    add: (params, fields, uuid) => mapSingleResponse(callOne(serviceCall('PART_PRICE_BREAK_ADD', params, fields, uuid))),
  }

  const partCostBreaks = {
    getOne: (id, fields, uuid) => getOne(partCostBreaks, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('PART_COST_BREAK_READ', params, fields, uuid)),
  }

  const partSuppliers = {
    getOne: (id, fields, uuid) => getOne(partSuppliers, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('PART_SUPPLIER_READ', params, fields, uuid)),
    add: (params, fields, uuid) => mapSingleResponse(callOne(serviceCall('PART_SUPPLIER_ADD', params, fields, uuid))),
  }

  const partCategories = {
    get: (params, fields, uuid) => callOne(serviceCall('PART_CATEGORY_READ', params, fields, uuid)),
    add: (params, fields, uuid) => mapSingleResponse(callOne(serviceCall('PART_CATEGORY_ADD', params, fields, uuid))),
    update: (params, fields, uuid) => callOne(serviceCall('PART_CATEGORY_UPDATE', params, fields, uuid)),
    delete: (params, fields, uuid) => mapSingleResponse(callOne(serviceCall('PART_CATEGORY_DELETE', params, fields, uuid))),
  }

  const partContents = {
    getOne: (id, fields, uuid) => getOne(partContents, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('PART_CONTENT_READ', params, fields, uuid)),
    add: (params, fields, uuid) => callOne(serviceCall('PART_CONTENT_ADD', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('PART_CONTENT_UPDATE', params, fields, uuid)),
    delete: (params, fields, uuid) => callOne(serviceCall('PART_CONTENT_DELETE', params, fields, uuid)),
  }

  const partTemplates = {
    get: (params, fields, uuid) => callOne(serviceCall('PART_TEMPLATE_READ', params, fields, uuid)),
  }

  const priceLists = {
    getOne: (id, fields, uuid) => getOne(priceLists, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('PRICE_LIST_READ', params, fields, uuid)),
    add: (params, fields, uuid) => callOne(serviceCall('PRICE_LIST_ADD', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('PRICE_LIST_UPDATE', params, fields, uuid)),
    delete: (params, fields, uuid) => callOne(serviceCall('PRICE_LIST_DELETE', params, fields, uuid)),
  }

  const pricingTemplates = {
    getOne: (id, fields, uuid) => getOne(pricingTemplates, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('PRICING_TEMPLATE_READ', params, fields, uuid)),
    add: (params, fields, uuid) => callOne(serviceCall('PRICING_TEMPLATE_ADD', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('PRICING_TEMPLATE_UPDATE', params, fields, uuid)),
    delete: (params, fields, uuid) => callOne(serviceCall('PRICING_TEMPLATE_DELETE', params, fields, uuid)),
  }

  const pricingGroups = {
    getOne: (id, fields, uuid) => getOne(pricingGroups, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('PRICING_GROUP_READ', params, fields, uuid)),
    add: (params, fields, uuid) => callOne(serviceCall('PRICING_GROUP_ADD', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('PRICING_GROUP_UPDATE', params, fields, uuid)),
    delete: (params, fields, uuid) => callOne(serviceCall('PRICING_GROUP_DELETE', params, fields, uuid)),
  }

  const printTemplates = {
    getOne: (id, fields, uuid) => getOne(printTemplates, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('PRINT_TEMPLATE_READ', params, fields, uuid)),
    add: (params, fields, uuid) => callOne(serviceCall('PRINT_TEMPLATE_ADD', params, fields, uuid)),
    duplicate: (params, fields, uuid) => callOne(serviceCall('PRINT_TEMPLATE_DUPLICATE', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('PRINT_TEMPLATE_UPDATE', params, fields, uuid)),
    delete: (params, fields, uuid) => callOne(serviceCall('PRINT_TEMPLATE_DELETE', params, fields, uuid)),
    validate: (params, fields, uuid) => callOne(serviceCall('PRINT_TEMPLATE_VALIDATE', params, fields, uuid)),
    getData: (params) => api.get('/template/data', params),
  }

  const printTemplateGroups = {
    getOne: (id, fields, uuid) => getOne(printTemplateGroups, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('PRINT_TEMPLATE_GROUP_READ', params, fields, uuid)),
    add: (params, fields, uuid) => callOne(serviceCall('PRINT_TEMPLATE_GROUP_ADD', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('PRINT_TEMPLATE_GROUP_UPDATE', params, fields, uuid)),
    delete: (params, fields, uuid) => callOne(serviceCall('PRINT_TEMPLATE_GROUP_DELETE', params, fields, uuid)),
  }

  const productionReasons = {
    get: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('PRODUCTION_REASON_READ', params, fields, uuid)),
    add: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('PRODUCTION_REASON_ADD', params, fields, uuid)),
    update: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('PRODUCTION_REASON_UPDATE', params, fields, uuid)),
    delete: (params = {}, fields = {default: true}, uuid) =>
      callOne(serviceCall('PRODUCTION_REASON_DELETE', params, fields, uuid)),
  }

  const departments = {
    getOne: (id, fields, uuid) => getOne(pricingGroups, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('DEPARTMENT_READ', params, fields, uuid)),
    add: (params = {}, fields = {default: true}, uuid) => callOne(serviceCall('DEPARTMENT_ADD', params, fields, uuid)),
    update: (params = {}, fields = {default: true}, uuid) => callOne(serviceCall('DEPARTMENT_UPDATE', params, fields, uuid)),
    delete: (params = {}, fields = {default: true}, uuid) => callOne(serviceCall('DEPARTMENT_DELETE', params, fields, uuid)),
  }

  const workCentres = {
    getOne: (id, fields, uuid) => getOne(pricingGroups, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('WORK_CENTRE_READ', params, fields, uuid)),
    add: (params = {}, fields = {default: true}, uuid) => callOne(serviceCall('WORK_CENTRE_ADD', params, fields, uuid)),
    update: (params = {}, fields = {default: true}, uuid) => callOne(serviceCall('WORK_CENTRE_UPDATE', params, fields, uuid)),
    delete: (params = {}, fields = {default: true}, uuid) => callOne(serviceCall('WORK_CENTRE_DELETE', params, fields, uuid)),
  }

  const sendAddresses = {
    get: (params, fields, uuid) => callOne(serviceCall('SEND_ADDRESS_READ', params, fields, uuid)),
    add: (params, fields, uuid) => callOne(serviceCall('SEND_ADDRESS_ADD', params, fields, uuid)),
    delete: (params, fields, uuid) => callOne(serviceCall('SEND_ADDRESS_DELETE', params, fields, uuid)),
    validate: (params, fields, uuid) => callOne(serviceCall('SEND_ADDRESS_RESEND_VALIDATION', params, fields, uuid)),
    setDefault: (params, fields, uuid) => callOne(serviceCall('SEND_ADDRESS_SET_DEFAULT', params, fields, uuid)),
  }

  const integrations = {
    getClaritySyncStatus: (uuid) => callOne(serviceCall('CLARITY_SYNC_STATUS', {}, {}, uuid)),
  }

  const licences = {
    get: (params, fields, uuid) => callOne(serviceCall('LICENSES_READ', params, fields, uuid)),
  }

  const accountLicences = {
    get: (params, fields, uuid) => callOne(serviceCall('ACCOUNT_LICENSE_READ', params, fields, uuid)),
  }

  const sendActivationEmail = {
    get: (params, fields, uuid) => callOne(serviceCall('EMAIL_ACTIVATION', params, fields, uuid)),
  }

  const references = {
    get: (params, fields, uuid) => callOne(serviceCall('REFERENCE_READ', params, fields, uuid)),
  }

  const accounts = {
    getPackages: (params, uuid) => callOne(serviceCall('ACCOUNT_PACKAGES_READ', {limit: 1000, ...params}, uuid)),
    getFeaturePackages: (params, fields, uuid) => callOne(serviceCall('PACKAGES_READ', params, fields), null, uuid),
  }

  const contactGroups = {
    get: (params, fields, uuid) => callOne(serviceCall('CONTACT_GROUP_READ', params, fields, uuid)),
    add: (params, fields = {}, uuid) => callOne(serviceCall('CONTACT_GROUP_ADD', params, fields, uuid)),
    update: (params, fields = {}, uuid) => callOne(serviceCall('CONTACT_GROUP_UPDATE', params, fields, uuid)),
    delete: (params, fields = {}, uuid) => callOne(serviceCall('CONTACT_GROUP_DELETE', params, fields, uuid)),
  }

  const contactRoles = {
    get: (params, fields, uuid) => callOne(serviceCall('CONTACT_ROLE_READ', params, fields, uuid)),
    add: (params, fields = {}, uuid) => callOne(serviceCall('CONTACT_ROLE_ADD', params, fields, uuid)),
    delete: (params, fields = {}, uuid) => callOne(serviceCall('CONTACT_ROLE_DELETE', params, fields, uuid)),
  }

  const contactMethods = {
    get: (params, fields, uuid) => callOne(serviceCall('CONTACT_METHOD_READ', params, fields, uuid)),
    add: (params, fields = {}, uuid) => callOne(serviceCall('CONTACT_METHOD_ADD', params, fields, uuid)),
    edit: (params, fields = {}, uuid) => callOne(serviceCall('CONTACT_METHOD_UPDATE', params, fields, uuid)),
    delete: (params, fields = {}, uuid) => callOne(serviceCall('CONTACT_METHOD_DELETE', params, fields, uuid)),
  }

  const settings = {
    changePassword: (current, password, uuid) => callOne(serviceCall('CHANGE_PASSWORD', {current, password}, {}, uuid)),
    sendActivationEmail: (userId, uuid) => callOne(serviceCall('EMAIL_ACTIVATION', {userId}, {}, uuid)),
    sendTFASetupEmail: (userId, uuid) => callOne(serviceCall('EMAIL_TFA_SETUP', {userId}, {}, uuid)),

    resetPassword: (userId, uuid) => callOne(serviceCall('RESET_PASSWORD', {userId}, {}, uuid)),
    addUser: (params, fields, uuid) => callOne(serviceCall('USER_LOGIN_ADD', params, {}, uuid)),
    updateUser: (params, fields, uuid) => callOne(serviceCall('USER_LOGIN_UPDATE', params, {}, uuid)),
    removeUser: (params, fields, uuid) => callOne(serviceCall('USER_LOGIN_REMOVE', params, {}, uuid)),
    userRoleUpdate: (params, fields, uuid) => callOne(serviceCall('USER_ROLE_UPDATE', params, {}, uuid)),

    uploadSignature: (formData, config) => api.post('binary/upload-signature', formData, config),
    uploadLogo: (formData, config) => api.post('binary/upload-logo', formData, config),
    deleteSignature: () => api.post('binary/delete-signature', {}, {}),
    deleteLogo: () => api.post('binary/delete-logo', {}, {}),
    addRolePermission: (params, fields, uuid) => callOne(serviceCall('ROLE_PERMISSION_ADD', params, {}, uuid)),
    deleteRolePermission: (params, fields, uuid) => callOne(serviceCall('ROLE_PERMISSION_DELETE', params, {}, uuid)),
    addRole: (params, fields, uuid) => callOne(serviceCall('ROLE_ADD', params, {}, uuid)),
    deleteRole: (params, fields, uuid) => callOne(serviceCall('ROLE_DELETE', params, {}, uuid)),
    currencyRead: (params, fields, uuid) => callOne(serviceCall('CURRENCY_READ', params, fields, uuid)),
    currencyUpdate: (params, fields, uuid) => callOne(serviceCall('CURRENCY_UPDATE', params, {}, uuid)),
    getGlobalCurrencies: (params, fields, uuid) => callOne(serviceCall('GET_GLOBAL_CURRENCIES', params, fields, uuid)),
    currencyEnable: (params, fields, uuid) => callOne(serviceCall('CURRENCY_ENABLE', params, fields, uuid)),
    currencyRemoteUpdate: (params, fields, uuid) => callOne(serviceCall('CURRENCY_REMOTE_UPDATE', params, fields, uuid)),
    referralFeeClear: (params, fields, uuid) => callOne(serviceCall('REFERRAL_FEE_DEFAULT_CLEAR', params, fields, uuid)),
    referralFeeApply: (params, fields, uuid) => callOne(serviceCall('REFERRAL_FEE_DEFAULT_SET', params, fields, uuid)),
    referralFeeRead: (params, fields, uuid) => callOne(serviceCall('REFERRAL_FEE_DEFAULT_READ', params, fields, uuid)),
    setCurrentModule: (module, uuid) => callOne(serviceCall('SET_CURRENT_MODULE', {module}, {}, uuid)),
  }

  const shopify = {
    authorise: (params, fields, uuid) => callOne(serviceCall('SHOPIFY_AUTHENTICATE', params, fields, uuid)),
    enabled: (params, fields, uuid) => callOne(serviceCall('SHOPIFY_ENABLED', params, fields, uuid)),
    disable: (params, fields, uuid) => callOne(serviceCall('SHOPIFY_DISABLE', params, fields, uuid)),
    status: (params, fields, uuid) => callOne(serviceCall('SHOPIFY_STATUS', params, fields, uuid)),
  }

  const accountSettings = {
    get: (params, fields, uuid) => callOne(serviceCall('ACCOUNT_SETTINGS_READ', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('ACCOUNT_SETTING_UPDATE', params, fields, uuid)),
  }

  const tfaSetting = {
    getCode: (params, fields, uuid) => callOne(serviceCall('TWO_FACTOR_GET_CODE', params, fields, uuid)),
    enable: (params, fields, uuid) => callOne(serviceCall('TWO_FACTOR_ENABLE', params, fields, uuid)),
    dismiss: (params, fields, uuid) => callOne(serviceCall('TWO_FACTOR_PROMPT_DISMISS', params, fields, uuid)),
    reset: (params, fields, uuid) => callOne(serviceCall('TWO_FACTOR_RESET', params, fields, uuid)),
    disableForUser: (params, fields, uuid) => callOne(serviceCall('TWO_FACTOR_DISABLE', params, fields, uuid)),
    disableForAll: (params, fields, uuid) => callOne(serviceCall('TWO_FACTOR_DISABLE_FOR_ALL', params, fields, uuid)),
  }

  const prefixes = {
    get: (params, fields, uuid) => callOne(serviceCall('PREFIX_READ', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('PREFIX_UPDATE', params, fields, uuid)),
  }

  const bulk = {
    addOrders: (params, fields, uuid) => callOne(serviceCall('BULK_ORDER_ADD', params, fields, uuid)),
    addActivities: (params, fields, uuid) => callOne(serviceCall('BULK_ACTIVITY_ADD', params, fields, uuid)),
  }

  const billingDetails = {
    get: (uuid) => callOne(serviceCall('BILLING_DETAILS_READ', {}, {}, uuid)),
    update: (params, value, uuid) => callOne(serviceCall('BILLING_DETAILS_UPDATE', params, {}, uuid)),
    getBillingInvoices: (params, fields, uuid) => callOne(serviceCall('BILLING_INVOICES_READ', params, fields, uuid)),
  }

  const salesTargets = {
    get: (params, fields, uuid) => callOne(serviceCall('SALES_TARGET_READ', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('SALES_TARGET_UPDATE', params, fields, uuid)),
  }

  const teams = {
    getOne: (id, fields, uuid) => getOne(teams, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('TEAM_READ', params, fields, uuid)),
    add: (params, fields, uuid) => callOne(serviceCall('TEAM_ADD', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('TEAM_UPDATE', params, fields, uuid)),
    delete: (params, fields, uuid) => callOne(serviceCall('TEAM_DELETE', params, fields, uuid)),
  }

  const teamMembers = {
    getOne: (id, fields, uuid) => getOne(teams, id, fields, uuid),
    get: (params, fields, uuid) => callOne(serviceCall('TEAM_MEMBERS_READ', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('TEAM_MEMBER_UPDATE', params, fields, uuid)),
  }

  const zoho = {
    authorise: (params) => callOne(serviceCall('ZOHO_AUTHENTICATE', params)),
    disable: () => callOne(serviceCall('ZOHO_DISABLE')),
    get: () => callOne(serviceCall('ZOHO_SYNC_READ')),
    update: (sync) => callOne(serviceCall('ZOHO_SYNC_UPDATE', sync)),
    getOrganisations: () => callOne(serviceCall('ZOHO_ORGANISATIONS_READ')),
    getWorkspaces: (params) => callOne(serviceCall('ZOHO_WORKSPACES_READ', params)),
    getRecords: (params) => callOne(serviceCall('ZOHO_SYNC_GET_RECORDS', params)),
    getPlan: (params) => callOne(serviceCall('ZOHO_PLAN_READ', params)),
    getSelectedTables: (params) => callOne(serviceCall('ZOHO_SELECTED_TABLE_READ', params)),
  }

  const publicApi = {
    acceptOrder: (date, user, account, code, order, image, width, height, name, orderNumber) =>
      api.post('accept/accept-order', {
        date, user, account, code, order, image, width, height, name, orderNumber,
      }),
    acceptPurchaseOrder: (date, user, account, code, purchaseOrder, image, width, height, name, orderNumber) =>
      api.post('accept/accept-purchase-order', {
        date, user, account, code, purchaseOrder, image, width, height, name, orderNumber,
      }),
  }

  const language = {
    translate: (params, fields, uuid) => callOne(serviceCall('LANGUAGE_TRANSLATE', params, fields, uuid)),
  }

  const document = {
    addFolder: (params, fields, uuid) => callOne(serviceCall('DOCUMENT_FOLDER_ADD', params, fields, uuid)),
    delete: (params, fields, uuid) => callOne(serviceCall('DOCUMENT_DELETE', params, fields, uuid)),
    getDrive: (params, fields, uuid) => callOne(serviceCall('DOCUMENT_DRIVE_READ', params, fields, uuid)),
    getFolder: (params, fields, uuid) => callOne(serviceCall('DOCUMENT_FOLDER_READ', params, fields, uuid)),
    getSite: (params, fields, uuid) => callOne(serviceCall('DOCUMENT_SITE_READ', params, fields, uuid)),
    getUploadUrl: (params, fields, uuid) => callOne(serviceCall('DOCUMENT_UPLOAD_URL_READ', params, fields, uuid)),
    renameFolder: (params, fields, uuid) => callOne(serviceCall('DOCUMENT_FOLDER_RENAME', params, fields, uuid)),
    updateBaseDrive: (params, fields, uuid) => callOne(serviceCall('DOCUMENT_DRIVE_BASE_UPDATE', params, fields, uuid)),
    updateBaseSite: (params, fields, uuid) => callOne(serviceCall('DOCUMENT_SITE_BASE_UPDATE', params, fields, uuid)),
    updateFolderBase: (params, fields, uuid) => callOne(serviceCall('DOCUMENT_FOLDER_UPDATE_BASE', params, fields, uuid)),
  }

  const approval = {
    add: (params, fields, uuid) => callOne(serviceCall('APPROVAL_ADD', params, fields, uuid)),
    get: (params, fields, uuid) => callOne(serviceCall('APPROVAL_READ', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('APPROVAL_UPDATE', params, fields, uuid)),
    getUploadUrl: (params, fields, uuid) => callOne(serviceCall('APPROVAL_GET_UPLOAD_URL', params, fields, uuid)),
    customerLinkRead: (params, fields, uuid) => callOne(serviceCall('APPROVAL_CUSTOMER_LINK_READ', params, fields, uuid)),
    addRevision: (params, fields, uuid) => callOne(serviceCall('APPROVAL_REVISION_ADD', params, fields, uuid)),
  }

  const automaticReminderConfigurations = {
    add: (params, fields, uuid) => callOne(serviceCall('AUTOMATIC_REMINDER_CONFIGURATION_ADD', params, fields, uuid)),
    get: (params, fields, uuid) => callOne(serviceCall('AUTOMATIC_REMINDER_CONFIGURATION_READ', params, fields, uuid)),
    update: (params, fields, uuid) => callOne(serviceCall('AUTOMATIC_REMINDER_CONFIGURATION_UPDATE', params, fields, uuid)),
    delete: (params, fields, uuid) => callOne(serviceCall('AUTOMATIC_REMINDER_CONFIGURATION_DELETE', params, fields, uuid)),
  }

  const authSettings = {
    get: () => callOne(serviceCall("AUTH_SETTINGS_READ")),
    update: (params) => callOne(serviceCall("AUTH_SETTINGS_UPDATE", params)),
  }

  const authUserSettings = {
    get: (params) => callOne(serviceCall("AUTH_USER_SETTINGS_READ", params)),
  }

  const supportTokens = {
    get: () => callOne(serviceCall("SUPPORT_TOKEN_READ")),
    add: (params) => callOne(serviceCall("SUPPORT_TOKEN_ADD", params)),
    delete: (params) => callOne(serviceCall("SUPPORT_TOKEN_DELETE", params)),
  }

  const pricingCalculatorAccessToken = {
    create: (params) => callOne(serviceCall("CREATE_ACCESS_TOKEN", params)),
  }

  const pricingCalculator = {
    run: (token, name, version, request) => {
      const calculatorApi = createPublicAPI('/calculator-service', token)
      return calculatorApi.post(`/calculators/${name}/${version}/run`, request)
    },
    getFields: (token, name, version) => {
      const calculatorApi = createPublicAPI('/calculator-service', token)
      return calculatorApi.get(`/calculators/${name}/${version}/fields`)
    },
    getForm: (token, name, version, level = INPUT_LEVELS.ITEM) => {
      const calculatorApi = createPublicAPI('/calculator-service', token)

      let url = `/calculators/${name}/${version}/form`
      if (level) url += '?level=' + level

      return calculatorApi.get(url)
    },
    getResourcesForm: (token) => {
      const calculatorApi = createPublicAPI('/calculator-service', token)
      return calculatorApi.get('/resources/form')
    },
    addResource: (token, resource) => {
      const calculatorApi = createPublicAPI('/calculator-service', token)
      return calculatorApi.post('/resources', resource)
    },
    listAll: (token) => {
      const calculatorApi = createPublicAPI('/calculator-service', token)
      return calculatorApi.get('/calculators')
    },
    getCalculatorInstance: (partId, calculatorName, calculatorVersion, fields) => {
      return callOne(serviceCall(
        'PRICING_CALCULATOR_READ',
        {
          partId,
          calculator: calculatorName + '@' + calculatorVersion,
        },
        fields
      ))
    },
    getMasterInstance: (calculatorName, calculatorVersion, fields) => {
      return callOne(serviceCall(
        'PRICING_CALCULATOR_READ',
        {masterCalculator: calculatorName + '@' + calculatorVersion},
        fields
      ))
    },
    addMasterInstance: (pricingCalculator) => {
      return callOne(serviceCall('PRICING_CALCULATOR_ADD', {pricingCalculator}))
    },
    updateMasterInstance: (pricingCalculator) => {
      return callOne(serviceCall('PRICING_CALCULATOR_UPDATE', {pricingCalculator}))
    },
    sendToSupport: (debugKey, calculatorName, calculatorVersion) => {
      return callOne(serviceCall(
        'PRICING_CALCULATOR_SEND_TO_SUPPORT',
        {debugKey, calculator: calculatorName + '@' + calculatorVersion}
      ))
    },
  }

  const viewApproval = (token) => {
    const api = createPublicAPI(baseURL, token)
    return api.get('customer/view-approval')
  }

  const customerViewUpdateStatus = (token, params) => {
    const api = createPublicAPI(baseURL, token)
    return api.post('customer/update-approval-status', params)
  }

  const resetPasswordUsingJWT = (token, password) => {
    const api = createPublicAPI(baseURL, token)
    return api.post('jwt/reset-password', {token, newPassword: password})
  }

  return {
    accounts,
    accounting,
    accountLicences,
    accountSettings,
    activity,
    activityTypes,
    activityResources,
    activityResourceTypes,
    activityReasons,
    activateUser,
    activateUserWithJwt,
    approval,
    automaticReminderConfigurations,
    authSettings,
    authUserSettings,
    axiosInstance: api.axiosInstance,
    billingDetails,
    bulk,
    businessTypes,
    call,
    campaigns,
    campaignGroups,
    config,
    contact,
    contactList,
    contactListCategory,
    contactGroups,
    contactRoles,
    contactsAndCompanies,
    contactImportJob,
    contactImportData,
    trustedDeviceData,
    contactMethods,
    company,
    companyGroups,
    countries,
    currency,
    customerViewUpdateStatus,
    deliveryMethods,
    deliveryNote,
    deliveryNoteItem,
    deliveryReasons,
    delivery,
    documents,
    document,
    documentFolderTemplates,
    emailing,
    fields,
    fieldsOptions,
    finaliseAccount,
    forgotPassword,
    getResponse,
    getSingleResponse,
    handleResponse,
    handleResponseNonYield,
    history,
    hubspot,
    integrations,
    invoice,
    invoiceHistory,
    invoiceItem,
    jobCards,
    language,
    licences,
    location,
    login,
    logout,
    authenticateTfaWithTotp,
    mailchimp,
    nominalCodes,
    notifications,
    oneDrive,
    opportunity,
    opportunityHistory,
    opportunityStage,
    opportunityReason,
    orderReasons,
    orderTypes,
    layouts,
    packTypes,
    parts,
    partPacks,
    partSuppliers,
    partPriceBreaks,
    partCostBreaks,
    partCategories,
    partContents,
    partTemplates,
    priceLists,
    pricingTemplates,
    pricingGroups,
    printTemplates,
    printTemplateGroups,
    priceListImportJob,
    priceListImportData,
    productionReasons,
    departments,
    workCentres,
    purchaseInvoice,
    purchaseInvoiceItem,
    purchaseOrder,
    purchaseOrderItem,
    purchaseOrderHistory,
    subscriptions,
    prefixes,
    products,
    public: publicApi,
    quickBooks,
    quote,
    quoteHistory,
    quoteAmendments,
    quoteStats,
    quoteItem,
    quoteRevision,
    references,
    refreshAuth,
    refreshAuthTotp,
    resetPassword,
    resetPasswordUsingJWT,
    salesTargets,
    sendActivationEmail,
    sendAddresses,
    sessionLogin,
    sessionCheck,
    settings,
    sharePoint,
    shopify,
    signUp_202011,
    state,
    supportTokens,
    pricingCalculatorAccessToken,
    pricingCalculator,
    resource,
    taxCode,
    textTemplates,
    teamMembers,
    teams,
    userFields,
    userFieldCategories,
    userLoad,
    viewApproval,
    xero,
    zoho,
    sage50,
    tfaSetting,
  }
}

export const api = create()

export default {
  create,
  api,
  createAPI,
}
